import { PropsWithChildren } from "react";
import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
} from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import '@mui/x-data-grid/themeAugmentation';

type ThemeProperties = PropsWithChildren<{}>;

const theme = createTheme({
  components: {
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        disableInteractive: true,
      },
      styleOverrides: {
        tooltip: {
          fontSize: "15px",
          borderRadius: "0.4rem",
        },
      },
    },

    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          // Remove default minus margin for FormControlLabel
          marginLeft: 0,
          marginRight: 0,
          gap: 5,
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: 0,
          marginRight: 5,
          position: 'relative',
          '& .MuiSvgIcon-root': {
            border: '1px solid #9ca3af',
            borderRadius: 6,
            color: 'transparent',
            width: 22,
            height: 22,
            backgroundColor: 'transparent',
          },
          '&:hover .MuiSvgIcon-root': {
            borderColor: '#2563eb',
          },
          '&.Mui-checked .MuiSvgIcon-root, &.MuiCheckbox-indeterminate .MuiSvgIcon-root': {
            backgroundColor: '#2563eb',
            borderColor: '#2563eb',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) scale(0) rotate(45deg)',
            transition: 'transform 0.2s ease-in-out',
            width: 14,
            height: 14,
            backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path d="M21 7L9 19L3.5 13.5" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>')`,
          },
          '&.Mui-checked::after': {
            transform: 'translate(-50%, -50%) scale(1) rotate(0deg)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) scale(0)',
            transition: 'transform 0.2s ease-in-out',
            width: 10,
            height: 2,
            backgroundColor: 'white',
            borderRadius: 1,
          },
          '&.MuiCheckbox-indeterminate::before': {
            transform: 'translate(-50%, -50%) scale(1)',
          },
          '&.MuiCheckbox-indeterminate::after': {
            transform: 'translate(-50%, -50%) scale(0) rotate(45deg)',
          },
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 5,
        },
        switchBase: {
          '&.Mui-checked': {
            '& + .MuiSwitch-track': {
              backgroundColor: 'var(--color-accent)',
              opacity: 1,
              border: 0,
            },
            '& .MuiSwitch-thumb': {
              color: '#fff',
            },
          },
        },
        thumb: {
          boxShadow: 'none',
        },
        track: {
          borderRadius: 300,
          backgroundColor: 'var(--color-accent-light)',
          opacity: 1,
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: "16px",
          height: "40px",
          padding: "0 5px 0 10px",
          borderRadius: "52px",
          "& .MuiChip-label": {
            padding: "0 8px",
            fontSize: "16px",
            fontWeight: 500,
          },
          "& .MuiChip-deleteIcon": {
            fontSize: "20px",
            marginLeft: "8px",
          },
        },
        filled: {
          backgroundColor: "#0175FF",
          color: "#ffffff",
          "& .MuiChip-deleteIcon": {
            color: "#ffffff",
            "&:hover": {
              color: "#e3f2fd",
            },
          },
        },
        outlined: {
          border: "1px solid #d6dde0",
        },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          borderRadius: "var(--border-radius)",
          backgroundColor: "white",
          fontSize: "16px",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#F4F8FB",
            borderRadius: "0",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold",
            alignItems: "center",
          },

          // Remove the seperator line between the header items
          "& .MuiDataGrid-columnSeparator": {
            pointerEvents: "none",
            visibility: "hidden",
          },

          // Remove focus outline
          "& .MuiDataGrid-cell, & .MuiDataGrid-row, & .MuiDataGrid-columnHeader, & .MuiDataGrid-cellCheckbox, & .MuiDataGrid-columnHeaderCheckbox, & .MuiButtonBase-root, & .MuiButton-root": {
            "&:focus, &:focus-within": {
              outline: "none",
            },
          },
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: "var(--border-radius) !important",
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        startIcon: {
          marginRight: "12px",
          "& > *:first-of-type": {
            fontSize: "16px",
          },
        },
        endIcon: {
          marginLeft: "12px",
          "& > *:first-of-type": {
            fontSize: "16px",
          },
        },
        root: {
          textTransform: "none",
          boxShadow: "none",
          padding: "8px 30px",
          fontSize: "16px",
          borderRadius: "var(--border-radius)",
        },
        contained: {
          backgroundColor: "var(--color-accent)",
          color: "#fff",
          "&:hover": {
            backgroundColor: "var(--color-accent-light)",
            boxShadow: "none",
          },
        },
        outlined: {
          borderColor: "var(--color-accent)",
          color: "var(--color-accent)",
          "&:hover": {
            borderColor: "var(--color-accent-light)",
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        },
        text: {
          color: "#0275ff",
          padding: "5px 10px",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        },
      },
    },
  },

  typography: {
    fontFamily: ["Inter", "sans-serif"].join(","),
  },

  palette: {
    primary: {
      main: "#0175FF",
    },
    secondary: {
      main: "#f2f2f2",
    },
  },
});

export default function Theme({ children }: ThemeProperties) {
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
